/**
 * Все числа прогона одним блоком — на поверхности разработчика (docs/UI_PLAN.md §2.3).
 *
 * Раньше блок был адресуемым по группам: оптика раскрывалась в части 2, радар и решение — в
 * части 3. После разреза поверхностей адресат у всех трёх групп один и тот же — мы, — и
 * второго места, куда его встроить, не осталось: три группы, три подписи, один раскрытый
 * блок в разделе «Числа». Всё читается прямо с payload прогона, без производных статистик.
 */

import { T } from "../strings";
import type { Run } from "../api/types";
import { formatDate, integer, isFiniteNumber, num, signed } from "../lib/format";

const Row = ({ label, value }: { label: string; value: string }) => (
  <>
    <dt>{label}</dt>
    <dd>{value}</dd>
  </>
);

const yesNo = (value: unknown): string =>
  value === true ? T.evidence.yes : value === false ? T.evidence.no : T.common.noData;

interface GroupProps {
  run: Run;
}

const GroupLabel = ({ text }: { text: string }) => (
  <div className="k-label" style={{ marginTop: "var(--gap-3)" }}>
    {text}
  </div>
);

/**
 * Only what part 2 does NOT already say. Current NDVI, the 30-day change, the local maximum,
 * the drop from it, the accepted-date count, the class and the confidence are the readout of
 * part 2 itself — repeating them here would make the same number look like two measurements.
 */
function OpticalGroup({ run }: GroupProps) {
  const result = run.result;
  const optical = run.payload?.optical_state ?? null;

  return (
    <>
      <GroupLabel text={T.evidence.optical} />
      <dl className="k-readout">
        <Row
          label={T.evidence.trendSlope}
          value={signed(optical?.ndvi_theil_sen_slope_per_day, 4)}
        />
        <Row label={T.evidence.kendall} value={num(optical?.ndvi_kendall_tau, 2)} />
        <Row
          label={T.evidence.filteredAcquisitions}
          value={integer(optical?.scene_filtered_acquisitions)}
        />
        <Row label={T.evidence.latestClear} value={formatDate(result?.latest_optical_date)} />
        <Row label={T.evidence.dataAge} value={integer(result?.optical_age_days)} />
        <Row label={T.evidence.ndmiChange30} value={signed(optical?.ndmi_change_per_30d, 3)} />
        <Row label={T.evidence.bsiChange30} value={signed(optical?.bsi_change_per_30d, 3)} />
      </dl>

      {isFiniteNumber(optical?.robust_current_bsi) && (
        <p className="k-hint k-mono" style={{ marginTop: "var(--gap-1)" }}>
          BSI {num(optical?.robust_current_bsi, 3)} · NDMI {num(optical?.robust_current_ndmi, 3)} ·
          NDTI {num(optical?.robust_current_ndti, 3)} · {T.evidence.robustPoints}{" "}
          {integer(optical?.robust_points)}
        </p>
      )}
    </>
  );
}

function RadarGroup({ run }: GroupProps) {
  const payload = run.payload ?? null;
  const radar = payload?.radar_state ?? null;
  const best = payload?.result?.best_event ?? null;
  // The counts of the OBSERVED series, when the decider never ran. Same numbers the
  // series block shows, so the two cannot disagree on one screen.
  const observation = payload?.radar_observation ?? null;

  const bestNumber = (key: string, digits = 2): string => {
    const value = best?.[key];
    return isFiniteNumber(value) ? num(value, digits) : T.common.noData;
  };

  return (
    <>
      <GroupLabel text={T.evidence.radar} />
      <dl className="k-readout">
        {/* One label, value yes/no: a label that changes with its own value reads as two
            different measurements. The skip reason is its own line below the readout. */}
        <Row label={T.evidence.radarStarted} value={yesNo(payload?.radar_started)} />
        <Row
          label={T.evidence.radarAcquisitions}
          value={integer(
            radar?.sentinel1_acquisitions ??
              observation?.acquisitions_accepted ??
              payload?.rows_used?.radar,
          )}
        />
        <Row
          label={T.evidence.radarTracks}
          value={integer(radar?.evaluated_tracks ?? observation?.tracks?.length)}
        />
        <Row
          label={T.evidence.radarClass}
          value={radar?.radar_class_ru ?? radar?.radar_class ?? T.common.noData}
        />
        <Row label={T.evidence.rfdiChange} value={bestNumber("rfdi_change_median_pp")} />
        <Row label={T.evidence.b11Growth} value={bestNumber("b11_growth_pct", 1)} />
        <Row label={T.evidence.risingOrbits} value={bestNumber("rising_orbits", 0)} />
      </dl>
      {payload?.radar_skip_reason && (
        <p className="k-caption" style={{ marginTop: "var(--gap-1)" }}>
          {payload.radar_skip_reason}
        </p>
      )}
      {/* Said here too: these rows sit next to the decision numbers, and a count of radar
          acquisitions in that neighbourhood reads as an input unless it is denied. */}
      {observation !== null && <p className="k-caption">{T.radar.notice}</p>}
    </>
  );
}

function DecisionGroup({ run }: GroupProps) {
  const result = run.result;
  const payload = run.payload ?? null;
  const field = payload?.field ?? null;

  return (
    <>
      <GroupLabel text={T.evidence.decision} />
      <dl className="k-readout">
        <Row label={T.evidence.eventFrom} value={formatDate(result?.event_from)} />
        <Row label={T.evidence.eventTo} value={formatDate(result?.event_to)} />
        <Row label={T.evidence.dryState} value={yesNo(payload?.dry?.confirmed)} />
        <Row label={T.evidence.dryDays} value={integer(result?.dry_days)} />
        <Row label={T.evidence.area} value={`${num(field?.area_ha, 1)} ${T.tools.hectares}`} />
        <Row
          label={T.evidence.analysisArea}
          value={`${num(field?.analysis_area_ha, 1)} ${T.tools.hectares}`}
        />
        <Row label={T.evidence.metricCrs} value={field?.metric_crs ?? T.common.noData} />
        <Row label={T.evidence.innerBuffer} value={yesNo(field?.inner_buffer_applied)} />
        {/* `gee_calls`, `code_version`, `main_answer` и `why` — раздел «Провенанс» той же
            панели: здесь измерения решения, там подпись под прогоном. */}
      </dl>
    </>
  );
}

interface Props {
  run: Run;
  open?: boolean;
}

export function Evidence({ run, open = false }: Props) {
  return (
    <details className="k-details k-section k-section--tight" open={open}>
      <summary>{T.evidence.title}</summary>
      <OpticalGroup run={run} />
      <RadarGroup run={run} />
      <DecisionGroup run={run} />
    </details>
  );
}
